/* PreNumbers PSBB — Dashboard (reskinned).

   getUpdatedCalculations + state + the /api/psbb/getCalculationData wiring
   (including useInvestorReadyFinancial and setDataForExport) are unchanged. The
   old absolutely-positioned icon rail (DashboardSideBar / SidebarButton) is
   replaced by the collapsible FineTuneRail, and the sections are reskinned to
   the .pn-* design system, mirroring Financial Builder & Critical Finance. */

import { useState, useContext, useEffect, useCallback } from 'react';

import { DollarOutlined, TableOutlined, ProductFilled, DollarCircleFilled, BookFilled, SettingFilled, CalculatorFilled,
  RiseOutlined, PieChartOutlined, FundOutlined, ThunderboltOutlined } from '@ant-design/icons';

import { PSSBPageContext } from '../PSBBPage.jsx';
import { genericPOSTDatabaseCall, ShowAndHide, RenderOneTable, RenderMultiSelectTable } from '../../helperFunctions.jsx';
import { FineTuneRail, FineTuneItem, PageHead, FbIcons } from '../../FinancialBuilder/fbUI.jsx';
import { psbbOtherVariablesRenderFields } from '../../constants.jsx';
import { EntityDetails } from '../EntitiesPage/EntitiesPage.jsx';

import { RevenueVsCostChart, FinancialProjectionChart, RevenueGrowthChart, CostOverviewChart, BurnRateChart } from '../../FinancialBuilder/Charts/Charts.js';

import InvestmentEquityModal from '../../CriticalFinance/Misc/InvestmentEquityModal.jsx';
import CapTableSettingsModal from '../../CriticalFinance/Misc/CapTableSettingsModal.jsx';
import ProductsPage from '../../PSBB/ProductsPage/ProductsPage.jsx';
import FixedCostsPage from '../../FinancialBuilder/FixedCostsPage/FixedCostsPage.jsx';
import OtherVariablesCost from '../../FinancialBuilder/Misc/OtherVariblesCost.jsx';
import CostAllocations from '../../FinancialBuilder/Misc/CostAllocations.jsx';

const { ArrowLeftOutlined, ArrowRightOutlined } = FbIcons;

const KPI_ICONS = [<RiseOutlined />, <PieChartOutlined />, <FundOutlined />, <ThunderboltOutlined />];

function KpiCards({ stat }) {
  if (!stat || !stat[0]) return null;
  return (
    <div className="pn-kpis">
      {stat[0].map((k, i) => (
        <div key={k} className={"pn-kpi" + (i === 0 ? "" : "")}>
          <div className="kh"><span className="ic">{KPI_ICONS[i % KPI_ICONS.length]}</span>{k}</div>
          <div className="kv">{stat[1][i]}</div>
          <div className="kd up">firm-wide</div>
        </div>
      ))}
    </div>
  );
}

function DashboardSideBar({ onClickUpdateCalc }) {
  return (
    <FineTuneRail>
      <FineTuneItem name="Financials" icon={<DollarOutlined />} itemInModal={<InvestmentEquityModal contextToUse={PSSBPageContext} usedAt="psbb" />} />
      <FineTuneItem name="Data Tables" icon={<TableOutlined />} itemInModal={<CapTableSettingsModal contextToUse={PSSBPageContext} />} />
      <FineTuneItem name="Products" icon={<ProductFilled />} itemInModal={<ProductsPage showNavigationControls={false} />} />
      <FineTuneItem name="Cost Allocations" icon={<DollarCircleFilled />} itemInModal={<CostAllocations contextToUse={PSSBPageContext} />} />
      <FineTuneItem name="Fixed Costs" icon={<BookFilled />} itemInModal={<FixedCostsPage showNavigationControls={false} contextToUse={PSSBPageContext} />} />
      <FineTuneItem name="Other Variables" icon={<SettingFilled />} itemInModal={<OtherVariablesCost renderFields={psbbOtherVariablesRenderFields} contextToUse={PSSBPageContext} />} />
      <div className="pn-rail-sep" />
      <FineTuneItem name="Calculate" isModal={false} icon={<CalculatorFilled />} onClickUpdateCalc={onClickUpdateCalc} />
    </FineTuneRail>
  );
}

function CapTable({ data }) {
  if (!data || data.length === 0) return <div className="pn-fc-empty">No cap table data available.</div>;
  return (
    <div style={{ overflowX: 'auto', marginBottom: 20 }}>
      <table className="entity-table">
        <thead>
          <tr>
            <th>Shareholders</th>
            <th>Share %</th>
            <th>USD (PV)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index}>
              <td>{item.name}</td>
              <td>{item.sharePercentage}</td>
              <td>{item.presentValue}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Dashboard() {
  const { goTo, entitySelected, messageApi, setDataForExport, dataForExport, useInvestorReadyFinancial } = useContext(PSSBPageContext);
  const [dataLoading, setDataLoading] = useState(true);
  const [calculationData, setCalculationData] = useState({
    investment_data: [],
    share_price_data: [],
    valuation_at_ipo: [],
    cap_table_data: [],
  });
  const [formattedData, setFormattedData] = useState([["A", "A"], ["A", "A"]]);
  const [percentageData, setPercentageData] = useState([["A", "A"], ["A", "A"]]);
  const [firm_wide_data, setFirm_wide_data] = useState({});
  const [productAnalysisData, setProductAnalysisData] = useState([]);
  const [kpiCardsStat, setKpiCardsStat] = useState([]);

  const getUpdatedCalculations = useCallback(async () => {
    messageApi.loading('Fetching updated calculations...', 0);
    try {
      const response = await genericPOSTDatabaseCall('/api/psbb/getCalculationData', { entity: entitySelected, useInvestorReadyFinancial: useInvestorReadyFinancial }, "calculation data");
      setCalculationData(response.data);
      setDataForExport({ ...response.data, 'currency': entitySelected.out_currency }); // Set data for export
      setFormattedData(response.data.formattedData);
      setPercentageData(response.data.percentage_data);
      setFirm_wide_data({
        firmWideStat: response.data.firm_wide_stat,
        firmWideStatPercentage: response.data.firm_wide_stat_percentage,
        otherFirmWideStat: response.data.other_firm_wide_stat,
        arrRatiosFirmWide: response.data.arr_ratios_firm_wide,
      });
      setProductAnalysisData(response.data.productAnalyses);
      // Set kpi cards
      var kpi_cards_info = JSON.parse(JSON.stringify(response.data.firm_wide_stat_percentage));
      kpi_cards_info[0].splice(3, 2, 'Runway (Months)', 'Firm Cost Efficiency %' ,'Market Cap at IPO', 'Investment Amount');
      kpi_cards_info[1].splice(3, 2, response.data.firm_wide_stat[1][5], response.data.other_firm_wide_stat[1][3] ,response.data.valuation_at_ipo[1][0], response.data.investment_data[1][1]);
      
      setKpiCardsStat(kpi_cards_info);
      messageApi.destroy();
      messageApi.success('Calculations updated successfully');
    } catch (error) {
      console.error('Error fetching calculation data:', error);
      messageApi.destroy();
      messageApi.error('Failed to fetch updated calculations');
    }
    setDataLoading(false); // Only set to false after first load
  }, [entitySelected, messageApi, setDataForExport, useInvestorReadyFinancial]);

  useEffect(() => {
    getUpdatedCalculations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finalDATA = { formattedData, percentageData };

  return (
    <div className="pn-wrap fb-dashboard-overview">
      <PageHead eyebrow={entitySelected?.name} title="Summary"
        sub={<>Firm-wide projections, valuations &amp; equity. Use <b>Fine-tune</b> to adjust inputs, then recalculate.</>}>
        <button type="button" className="pn-btn pn-btn-ghost" onClick={() => goTo(2)}><ArrowLeftOutlined /> Back to Products</button>
        <button type="button" className="pn-btn pn-btn-cta" disabled={!dataForExport} onClick={() => goTo(4)}>Continue to Export <ArrowRightOutlined /></button>
      </PageHead>

      {!dataLoading && <KpiCards stat={kpiCardsStat} />}

      <ShowAndHide title={"Entity Info"}>
        <div className="pn-card" style={{ cursor: 'default', padding: 20 }}>
          <EntityDetails entity={entitySelected} editable={false} />
        </div>
      </ShowAndHide>

      {!dataLoading && (
        <>
          <ShowAndHide defaultOpen title="Firm-wide Financials">
            <div className="pn-fw-tables">
              <RenderOneTable name={"Firm Wide Stat %"} data={firm_wide_data.firmWideStatPercentage} />
              <RenderOneTable name={"Employees & Price Firm Wide Stat %"} data={firm_wide_data.firmWideStat} />
              <RenderOneTable name={"Other Firm Wide Stat"} data={firm_wide_data.otherFirmWideStat} />
              <RenderOneTable name={"ARR Ratios Firm Wide"} data={firm_wide_data.arrRatiosFirmWide} />
            </div>
          </ShowAndHide>

          <ShowAndHide defaultOpen title="Projections & Growth">
            <RenderMultiSelectTable data={formattedData} />
            <RenderMultiSelectTable data={percentageData} />
          </ShowAndHide>

          <ShowAndHide defaultOpen title="Valuations, Investments, Pricing and Equity">
            <div className="pn-fw-tables">
              <RenderOneTable data={calculationData.investment_data} name={"Investment Consideration"} />
              <RenderOneTable data={calculationData.share_price_data} name={"Price your Share"} />
            </div>
          </ShowAndHide>

          <ShowAndHide defaultOpen title="Valuations & Cap Tables (@IPO)">
            <div className="pn-fw-tables">
              <RenderOneTable data={calculationData.valuation_at_ipo} name={"Valuation at IPO"} />
              <CapTable data={calculationData.cap_table_data} />
            </div>
          </ShowAndHide>

          <ShowAndHide defaultOpen title="Product Analysis">
            <div className="pn-fw-tables">
              {productAnalysisData.map((productAnalysis, index) => (
                <RenderOneTable key={index} data={productAnalysis.data} name={`Product: ${productAnalysis.name}`} />
              ))}
            </div>
          </ShowAndHide>

          <div className="pn-charts">
            <ChartCard full title="Financial Projection" sub="Revenue · Cost · Net income">
              <FinancialProjectionChart finalDATA={finalDATA} />
            </ChartCard>
            <ChartCard title="Revenue Growth by Product %" sub="Per-product growth">
              <RevenueGrowthChart finalDATA={finalDATA} />
            </ChartCard>
            <ChartCard title="Fixed vs Variable Cost" sub="Cost structure over time">
              <CostOverviewChart finalDATA={finalDATA} />
            </ChartCard>
            <ChartCard title="Revenue vs Cost of Revenue %" sub="Revenue bars, cost % line">
              <RevenueVsCostChart finalDATA={finalDATA} />
            </ChartCard>
            <ChartCard title="Monthly Burn Rate" sub="Burn $ and usage %">
              <BurnRateChart finalDATA={finalDATA} />
            </ChartCard>
          </div>
        </>
      )}

      <DashboardSideBar onClickUpdateCalc={getUpdatedCalculations} />
    </div>
  );
}

function ChartCard({ title, sub, children, full }) {
  return (
    <div className={"pn-chart-card" + (full ? " full" : "")}>
      <div className="pn-chart-head"><div><div className="pn-chart-title">{title}</div>{sub && <div className="pn-chart-sub">{sub}</div>}</div></div>
      <div className="pn-chart-body">{children}</div>
    </div>
  );
}

export default Dashboard;
